import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, MapPin, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { COMPLIANCE_TEXT, LOGO } from "@/lib/assets";

export const ComplianceBanner = () => (
  <div data-testid="compliance-disclaimer-banner" className="bg-navy-900 text-slate-300 text-xs sm:text-sm leading-relaxed px-4 sm:px-8 lg:px-12 py-6 border-t border-white/10">
    <div className="max-w-5xl mx-auto flex items-start gap-3">
      <span className="mt-0.5 inline-block h-2 w-2 rounded-full bg-verified shrink-0" />
      <p>{COMPLIANCE_TEXT}</p>
    </div>
  </div>
);

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message);
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-navy-900 text-slate-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-1">
          <div className="mb-4">
            <img src={LOGO} alt="Millionaire Mindsetters" data-testid="footer-logo" className="h-12 w-auto object-contain" />
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-gold-light mt-1">Foundation</div>
          </div>
          <p className="text-sm text-slate-400">Opportunity Starts Here. A 501(c)(3) foundation connecting communities to education, assistance, and meaningful opportunities.</p>
        </div>

        <div>
          <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Explore</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/marketplace" className="hover:text-gold-light">Opportunity Marketplace</Link></li>
            <li><Link to="/housing" className="hover:text-gold-light">Housing Center</Link></li>
            <li><Link to="/financial-wellness" className="hover:text-gold-light">Financial Wellness</Link></li>
            <li><Link to="/grants" className="hover:text-gold-light">Grants & Funding</Link></li>
            <li><Link to="/events" className="hover:text-gold-light">Events</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Get Involved</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/donate" className="hover:text-gold-light">Donate</Link></li>
            <li><Link to="/volunteer" className="hover:text-gold-light">Volunteer</Link></li>
            <li><Link to="/apply" className="hover:text-gold-light">Program Applications</Link></li>
            <li><Link to="/sponsor" className="hover:text-gold-light">Corporate Sponsorship</Link></li>
            <li><Link to="/about" className="hover:text-gold-light">About Us</Link></li>
            <li><Link to="/founder" className="hover:text-gold-light" data-testid="footer-link-founder">Our Founder</Link></li>
          </ul>
          <div className="mt-5 space-y-2 text-sm text-slate-400">
            <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gold" /> Houston, Texas</p>
            <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-gold" /> info@mmfoundation.org</p>
          </div>
        </div>

        <div>
          <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Stay Connected</h4>
          <p className="text-sm text-slate-400 mb-3">Join our newsletter for new opportunities and events.</p>
          <form onSubmit={subscribe} className="space-y-3">
            <Input data-testid="newsletter-input-email" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="Your email"
              className="bg-white/10 border-white/20 text-white placeholder:text-slate-500" />
            <Button data-testid="newsletter-submit-btn" type="submit" disabled={loading}
              className="w-full rounded-full bg-gold hover:bg-gold-light text-white font-semibold">
              {loading ? "Subscribing..." : "Subscribe"}
            </Button>
          </form>
        </div>
      </div>
      <ComplianceBanner />
      <div className="border-t border-white/10 px-4 py-5 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} Millionaire Mindsetters Foundation. All rights reserved. ·{" "}
        <Link to="/login" className="hover:text-slate-300">Admin</Link>
      </div>
    </footer>
  );
};

export const SiteLayout = ({ children }) => (
  <>{children}<Footer /></>
);
